import { useState, useContext } from "react";
import axios from "axios";
import { AlertTriangle, Ban, Clock, VolumeX, MessageSquareWarning, X, Send, Shield, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { API, AuthContext } from "../App";

const ACTION_TYPES = [
  { id: "kick", name: "Kick", icon: "🚪", description: "Remove user from current room", color: "bg-yellow-500" },
  { id: "mute", name: "Mute", icon: "🔇", description: "Prevent user from sending messages", color: "bg-orange-500" },
  { id: "warn", name: "Warn", icon: "⚠️", description: "Issue a formal warning", color: "bg-blue-500" },
  { id: "temp_ban", name: "Temp Ban", icon: "⏱️", description: "Temporary suspension", color: "bg-red-400" },
  { id: "ban", name: "Permanent Ban", icon: "🚫", description: "Permanently ban user", color: "bg-red-600" },
  { id: "unmute", name: "Unmute", icon: "🔊", description: "Restore chat privileges", color: "bg-green-500" },
  { id: "unban", name: "Unban", icon: "✅", description: "Remove ban from user", color: "bg-green-600" },
];

const DURATION_PRESETS = [
  { id: "1h", name: "1 Hour", minutes: 60 },
  { id: "6h", name: "6 Hours", minutes: 360 },
  { id: "24h", name: "24 Hours", minutes: 1440 },
  { id: "7d", name: "7 Days", minutes: 10080 },
  { id: "30d", name: "30 Days", minutes: 43200 },
  { id: "custom", name: "Custom", minutes: null },
];

const ModerationActionModal = ({ targetUser, onClose, onActionComplete }) => {
  const { token, user } = useContext(AuthContext);
  const [actionType, setActionType] = useState("");
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const [notifyUser, setNotifyUser] = useState(true);
  const [showReasonToUser, setShowReasonToUser] = useState(false);
  const [durationPreset, setDurationPreset] = useState("24h");
  const [customDuration, setCustomDuration] = useState({ value: 1, unit: "hours" });
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = user?.is_admin;
  const selectedAction = ACTION_TYPES.find(a => a.id === actionType);

  const handleSubmit = async () => {
    if (!actionType) {
      toast.error("Please select an action type");
      return;
    }
    if (!reason.trim()) {
      toast.error("Please provide a reason");
      return;
    }

    setSubmitting(true);
    try {
      let custom_duration_minutes = null;
      if (actionType === "temp_ban" && durationPreset === "custom") {
        const multiplier = customDuration.unit === "hours" ? 60 : customDuration.unit === "days" ? 1440 : 1;
        custom_duration_minutes = customDuration.value * multiplier;
      }

      const payload = {
        target_user_id: targetUser.user_id,
        action_type: actionType,
        reason: reason.trim(),
        evidence: evidence.trim() || null,
        notify_user: notifyUser,
        show_reason_to_user: showReasonToUser,
        duration_preset: actionType === "temp_ban" && durationPreset !== "custom" ? durationPreset : null,
        custom_duration_minutes: custom_duration_minutes,
      };

      const response = await axios.post(`${API}/moderation/request-action`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === "executed") {
        toast.success(`Action executed: ${actionType} on ${targetUser.username}`);
      } else {
        toast.success(`Action requested. Awaiting admin approval.`);
      }

      onActionComplete?.();
      onClose?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit action");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4" onClick={onClose}>
      <div 
        className="bg-[#C0C0C0] rounded-lg shadow-2xl w-full max-w-lg overflow-hidden border-4 border-[#808080]"
        onClick={e => e.stopPropagation()}
        data-testid="moderation-action-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            <span className="font-bold">Moderation Action</span>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 p-1 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Target User Info */}
        <div className="bg-gray-100 px-4 py-2 border-b border-gray-300">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Target:</span>
            <span className="font-bold text-red-600">{targetUser?.username}</span>
            {targetUser?.is_banned && <span className="text-xs bg-red-500 text-white px-1 rounded">BANNED</span>}
            {targetUser?.is_muted && <span className="text-xs bg-orange-500 text-white px-1 rounded">MUTED</span>}
          </div>
        </div>

        {/* Form */}
        <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Action Type Selection */}
          <div>
            <label className="text-sm font-bold block mb-2">Action Type *</label>
            <div className="grid grid-cols-2 gap-2">
              {ACTION_TYPES.map(action => (
                <button
                  key={action.id}
                  onClick={() => setActionType(action.id)}
                  className={`p-2 rounded border-2 text-left transition-all ${
                    actionType === action.id 
                      ? `${action.color} text-white border-gray-800` 
                      : 'bg-white border-gray-300 hover:border-gray-500'
                  }`}
                  data-testid={`action-${action.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span>{action.icon}</span>
                    <span className="font-bold text-sm">{action.name}</span>
                  </div>
                  <p className="text-[10px] mt-1 opacity-80">{action.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Duration for Temp Ban */}
          {actionType === "temp_ban" && (
            <div>
              <label className="text-sm font-bold block mb-2">Ban Duration *</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {DURATION_PRESETS.map(preset => (
                  <button
                    key={preset.id}
                    onClick={() => setDurationPreset(preset.id)}
                    className={`px-3 py-1 rounded text-sm ${
                      durationPreset === preset.id
                        ? 'bg-red-500 text-white'
                        : 'bg-white border border-gray-300 hover:border-red-400'
                    }`}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
              {durationPreset === "custom" && (
                <div className="flex gap-2 items-center">
                  <input
                    type="number"
                    min="1"
                    value={customDuration.value}
                    onChange={e => setCustomDuration(prev => ({ ...prev, value: parseInt(e.target.value) || 1 }))}
                    className="retro-input w-20 text-sm"
                  />
                  <select
                    value={customDuration.unit}
                    onChange={e => setCustomDuration(prev => ({ ...prev, unit: e.target.value }))}
                    className="retro-input text-sm"
                  >
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="text-sm font-bold block mb-2">Reason for Action *</label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Explain why you're taking this action..."
              className="retro-input w-full h-24 text-sm resize-none"
              data-testid="reason-input"
            />
          </div>

          {/* Evidence */}
          <div>
            <label className="text-sm font-bold block mb-2">Evidence / Additional Notes</label>
            <textarea
              value={evidence}
              onChange={e => setEvidence(e.target.value)}
              placeholder="Screenshots, links, chat logs, etc. (optional)"
              className="retro-input w-full h-16 text-sm resize-none"
              data-testid="evidence-input"
            />
          </div>

          {/* Notification Options */}
          <div className="space-y-2 bg-gray-100 p-3 rounded">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={notifyUser}
                onChange={e => setNotifyUser(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm">Notify user about this action</span>
            </label>
            
            {notifyUser && (
              <label className="flex items-center gap-2 cursor-pointer ml-6">
                <input
                  type="checkbox"
                  checked={showReasonToUser}
                  onChange={e => setShowReasonToUser(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm flex items-center gap-1">
                  {showReasonToUser ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  Show reason to user
                </span>
              </label>
            )}
          </div>

          {/* Approval Notice */}
          {!isAdmin && (
            <div className="bg-yellow-100 border border-yellow-400 rounded p-3 text-sm">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-yellow-800">Approval Required</p>
                  <p className="text-yellow-700 text-xs">
                    As a moderator/employee, this action requires admin approval before execution.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-200 border-t border-gray-400 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2 bg-gray-300 hover:bg-gray-400 rounded font-medium text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !actionType || !reason.trim()}
            className={`flex-1 py-2 rounded font-bold text-white text-sm flex items-center justify-center gap-2 ${
              submitting || !actionType || !reason.trim()
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700'
            }`}
            data-testid="submit-action-btn"
          >
            <Send className="w-4 h-4" />
            {submitting ? "Submitting..." : isAdmin ? "Execute Action" : "Request Approval"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModerationActionModal;
